import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";

export type ChatInputHandle = {
  blur: () => void;
  focus: () => void;
};

type Props = {
  value: string;
  isChatReady: boolean;
  onChange: (value: string) => void;
  onSend: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

const ChatInput = forwardRef<ChatInputHandle, Props>(
  ({ value, isChatReady, onChange, onSend, onFocus, onBlur }, ref) => {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const sentFromPointerRef = useRef(false);

    const resizeInput = useCallback(() => {
      const input = inputRef.current;
      if (!input) return;

      const computedStyle = window.getComputedStyle(input);

      const lineHeight = Number.parseFloat(computedStyle.lineHeight) || 20;

      const paddingTop = Number.parseFloat(computedStyle.paddingTop) || 0;

      const paddingBottom = Number.parseFloat(computedStyle.paddingBottom) || 0;

      const borderTop = Number.parseFloat(computedStyle.borderTopWidth) || 0;

      const borderBottom =
        Number.parseFloat(computedStyle.borderBottomWidth) || 0;

      const oneLineHeight =
        lineHeight + paddingTop + paddingBottom + borderTop + borderBottom;

      const twoLineHeight =
        lineHeight * 2 + paddingTop + paddingBottom + borderTop + borderBottom;

      // Reset so the textarea can shrink.
      input.style.height = "auto";
      input.style.overflowY = "hidden";

      const requiredHeight = input.scrollHeight + borderTop + borderBottom;

      const nextHeight = Math.max(
        oneLineHeight,
        Math.min(requiredHeight, twoLineHeight),
      );

      input.style.height = `${nextHeight}px`;

      const shouldScroll = requiredHeight > twoLineHeight;

      input.style.overflowY = shouldScroll ? "auto" : "hidden";

      if (shouldScroll) {
        requestAnimationFrame(() => {
          /*
           * Keep the newest text visible while allowing the user
           * to scroll upward manually afterward.
           */
          input.scrollTop = input.scrollHeight;
        });
      } else {
        input.scrollTop = 0;
      }
    }, []);

    useLayoutEffect(() => {
      resizeInput();
    }, [value, resizeInput]);

    useImperativeHandle(ref, () => ({
      blur: () => {
        inputRef.current?.blur();
      },

      focus: () => {
        inputRef.current?.focus({
          preventScroll: true,
        });
      },
    }));

    const sendAndKeepKeyboard = () => {
      onSend();

      requestAnimationFrame(() => {
        resizeInput();

        inputRef.current?.focus({
          preventScroll: true,
        });
      });
    };

    const handleFocus = () => {
      onFocus?.();

      window.setTimeout(() => {
        inputRef.current?.scrollIntoView({
          block: "nearest",
          inline: "nearest",
          behavior: "smooth",
        });
      }, 250);
    };

    const handleBlur = () => {
      onBlur?.();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      /*
       * Enter sends.
       * Shift + Enter inserts a new line.
       */
      if (
        event.key === "Enter" &&
        !event.shiftKey &&
        !event.nativeEvent.isComposing
      ) {
        event.preventDefault();

        if (!isChatReady) return;

        sendAndKeepKeyboard();
      }
    };

    return (
      <div className="input-container">
        <textarea
          ref={inputRef}
          value={value}
          rows={1}
          onChange={(event) => {
            onChange(event.target.value);

            requestAnimationFrame(() => {
              resizeInput();
            });
          }}
          onInput={() => {
            requestAnimationFrame(() => {
              resizeInput();
            });
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="Type your message..."
          className="input-message"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
        />

        <button
          type="button"
          disabled={!isChatReady}
          className="send-button"
          onPointerDown={(event) => {
            if (!isChatReady) return;

            /*
             * Prevent the button from stealing focus from the textarea,
             * especially on mobile.
             */
            event.preventDefault();

            sentFromPointerRef.current = true;
            sendAndKeepKeyboard();
          }}
          onClick={() => {
            /*
             * Prevent the click following pointerDown from sending twice.
             */
            if (sentFromPointerRef.current) {
              sentFromPointerRef.current = false;
              return;
            }

            /*
             * Preserve keyboard accessibility.
             */
            if (!isChatReady) return;

            sendAndKeepKeyboard();
          }}
        >
          Send
        </button>
      </div>
    );
  },
);

ChatInput.displayName = "ChatInput";

export default ChatInput;
